import { useContext, useEffect, useMemo, useCallback, useState } from "react";
import AttachDocumentIcon from "../assets/svg/AttachDocument";
import OnlineOrderIcon from "../assets/svg/OnlineOrderIcon";
import PatientResultIcon from "../assets/svg/PatientResultIcon";
import PickupIcon from "../assets/svg/PickupIcon";
import ErrorBoundary from "../components/global/ErrorBoundary";
import DashboardActionButton from "../components/molecular/DashboardActionButton";
import OverviewCard from "../components/molecular/OverviewCard";
import { PopUpContext } from "../services/context/popupContext";
import PatientSelectionPopup from "../components/popups/patientSelectionPopup";
import { useHistory } from "react-router-dom";
import useDashboardStatus from "../services/utils/custom hooks/useDashboardStatus";
import AdminContentLayout from "../layouts/AdminContentLayout";
import { ApplicationState } from "../services/context/ApplicationStateContext";
import { useOktaAuth } from "@okta/okta-react";
import OnlineOrderActions from "../services/OnlineOrderActions";
import useProfile from "../services/utils/custom hooks/useProfile";
import OrganizationAndClinicianSelection from "../components/popups/organizationAndClinicianSelectionPopup";
import CreatePatientPopup from "../components/popups/createPatientPopup";
import { useAdminContext } from "../services/context/TourPopupContext";
import useFeatureFlags from "../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import dataConstants from "../constants/dataConstants";
import NewReleasePopup from "../components/atomic/NewReleasePopup";
import NewVersionBanner from "../components/global/NewVersionBanner";
import useMobile from "../utils/helpers/useMobile";
import CustomText from "../components/atomic/CustomText";

function RenderLayout({
  getActionsForSection,
  isPLUUser,
  CardsList,
  isLoading,
  tooltipText,
  isMobile,
  handleButtonClick,
  isTourActive,
}: Readonly<{
  getActionsForSection: any;
  isPLUUser: boolean;
  CardsList: any[];
  isLoading: boolean;
  tooltipText: string;
  handleButtonClick: any;
  isMobile: boolean;
  isTourActive: boolean;
}>): JSX.Element {
  const history = useHistory();
  const ActionButtons = [
    {
      gradientInfo: {
        startColor: "#18B5B7",
        endColor: "#084E8B",
        hoverStart: "#31BDBF",
        hoverEnd: "#3F76A5",
        hoverMiddle: "#37A0B4",
      },
      title: "New Online Order",
      description: "Create a new medical order",
      key: dataConstants.SectionActionKeys.NEW_ONLINE_ORDER,
      Icon: (props: any) => (
        isMobile ? <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.2 14.5331C12.2293 14.5331 13.0667 13.5359 13.0667 12.3101V5.35277H0.933333V12.3101C0.933333 13.5359 1.77072 14.5331 2.8 14.5331H11.2ZM11.2 15.4665H2.8C1.2536 15.4665 0 14.0532 0 12.3101V4.41943H14V12.3101C14 14.0532 12.7464 15.4665 11.2 15.4665Z" fill="white" />
          <path fill-rule="evenodd" clip-rule="evenodd" d="M2.8 1.46654C2.31137 1.46654 1.92177 1.707 1.60894 2.20169C1.35841 2.59786 1.06877 3.30795 0.969141 4.53634H13.0667V3.82419C13.0667 3.16658 12.8544 2.55494 12.469 2.10195C12.1204 1.69219 11.6697 1.46654 11.2 1.46654H2.8ZM2.8 0.533203H11.2C12.7464 0.533203 14 2.00675 14 3.82419V5.46968H0C0 2.00675 1.2536 0.533203 2.8 0.533203Z" fill="white" />
          <path d="M11.2 3.33324C11.4577 3.33324 11.6667 3.1243 11.6667 2.86657C11.6667 2.60884 11.4577 2.3999 11.2 2.3999C10.9423 2.3999 10.7333 2.60884 10.7333 2.86657C10.7333 3.1243 10.9423 3.33324 11.2 3.33324Z" fill="white" />
          <path d="M9.33334 3.33324C9.59107 3.33324 9.8 3.1243 9.8 2.86657C9.8 2.60884 9.59107 2.3999 9.33334 2.3999C9.0756 2.3999 8.86667 2.60884 8.86667 2.86657C8.86667 3.1243 9.0756 3.33324 9.33334 3.33324Z" fill="white" />
          <path d="M6.53333 7.5332H3.73333C3.4756 7.5332 3.26667 7.74214 3.26667 7.99987C3.26667 8.2576 3.4756 8.46654 3.73333 8.46654H6.53333C6.79107 8.46654 7 8.2576 7 7.99987C7 7.74214 6.79107 7.5332 6.53333 7.5332Z" fill="white" />
          <path d="M3.73334 8H6.53334H3.73334Z" stroke="white" stroke-width="0.933333" />
          <path d="M9.8 11.2666H3.73333C3.4756 11.2666 3.26667 11.4755 3.26667 11.7333C3.26667 11.991 3.4756 12.1999 3.73333 12.1999H9.8C10.0577 12.1999 10.2667 11.991 10.2667 11.7333C10.2667 11.4755 10.0577 11.2666 9.8 11.2666Z" fill="white" />
          <path d="M3.73334 11.7334H9.8H3.73334Z" stroke="white" stroke-width="0.933333" />
        </svg>
          :
          <OnlineOrderIcon stroke={"#fff"} fill={"#fff"} {...props} />
      ),
    },
    {
      title: "Attach a Document",
      description: "Upload a medical document",
      key: dataConstants.SectionActionKeys.ATTACH_DOCUMENT,
      Icon: (props: any) => isMobile ? <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.99995 1V4.99992C9.99995 5.26517 10.1053 5.51955 10.2929 5.70711C10.4804 5.89467 10.7348 6.00004 11.0001 6.00004H15" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M12.9998 18.9999H3.00024C2.46974 18.9999 1.96097 18.7892 1.58586 18.4141C1.21074 18.0389 1 17.5302 1 16.9997V3.00024C1 2.46974 1.21074 1.96097 1.58586 1.58586C1.96097 1.21074 2.46974 1 3.00024 1H9.99996L15 6.00004V16.9997C15 17.2624 14.9483 17.5225 14.8477 17.7651C14.7472 18.0078 14.5999 18.2283 14.4141 18.4141C14.2284 18.5998 14.0079 18.7471 13.7652 18.8477C13.5225 18.9482 13.2624 18.9999 12.9998 18.9999Z" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.99992 7H6.00004" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.99992 11H11.0001" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.99992 15H11.0001" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
        : <AttachDocumentIcon {...props} />,
      onClick: (props: any) => history.push("/access/documents"),
    },
    {
      title: "Schedule a Pickup",
      description: "Arrange a sample collection",
      key: dataConstants.SectionActionKeys.SCHEDULE_PICKUP,
      Icon: (props: any) => isMobile ? <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.3752 7.91241C5.3752 8.43154 5.52914 8.93902 5.81756 9.37067C6.10598 9.80232 6.51592 10.1387 6.99554 10.3374C7.47516 10.5361 8.00292 10.5881 8.51208 10.4868C9.02125 10.3855 9.48894 10.1355 9.85603 9.76843C10.2231 9.40134 10.4731 8.93364 10.5744 8.42448C10.6757 7.91532 10.6237 7.38756 10.425 6.90794C10.2263 6.42831 9.88992 6.01838 9.45827 5.72996C9.02663 5.44154 8.51915 5.2876 8.00001 5.2876C7.30386 5.2876 6.63623 5.56414 6.14399 6.05639C5.65174 6.54863 5.3752 7.21626 5.3752 7.91241Z" stroke="#001F53" stroke-width="1.0109" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M12.9499 12.8627L9.23886 16.5752C8.91073 16.9031 8.46586 17.0872 8.00202 17.0872C7.53817 17.0872 7.0933 16.9031 6.76518 16.5752L3.05011 12.8627C2.07118 11.8837 1.40454 10.6364 1.13447 9.27857C0.864407 7.92071 1.00305 6.51327 1.53288 5.23421C2.0627 3.95515 2.95991 2.86192 4.11105 2.09277C5.26218 1.32362 6.61554 0.913086 8 0.913086C9.38445 0.913086 10.7378 1.32362 11.8889 2.09277C13.0401 2.86192 13.9373 3.95515 14.4671 5.23421C14.9969 6.51327 15.1356 7.92071 14.8655 9.27857C14.5955 10.6364 13.9288 11.8837 12.9499 12.8627Z" stroke="#001F53" stroke-width="1.0109" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
        : <PickupIcon {...props} />,
      onClick: () => history.push("/access/schedule-pickup"),
    },
    {
      title: "View a Patient Result",
      description: "Check test results",
      key: dataConstants.SectionActionKeys.VIEW_PATIENT_RESULT,
      Icon: (props: any) => isMobile ? <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.99993 3H3.00025C2.46976 3 1.96099 3.21074 1.58587 3.58586C1.21075 3.96097 1.00001 4.46974 1.00001 5.00024V17C1.00001 17.5305 1.21075 18.0393 1.58587 18.4144C1.77161 18.6001 1.99211 18.7475 2.23479 18.848C2.47747 18.9485 2.73758 19.0002 3.00025 19.0002H12.9998C13.2624 19.0002 13.5225 18.9485 13.7652 18.848C14.0079 18.7475 14.2284 18.6001 14.4142 18.4144C14.5999 18.2286 14.7472 18.0081 14.8478 17.7655C14.9483 17.5228 15 17.2627 15 17V5.00024C15 4.73757 14.9483 4.47746 14.8478 4.23478C14.7472 3.9921 14.5999 3.7716 14.4142 3.58586C14.2284 3.40012 14.0079 3.25278 13.7652 3.15226C13.5225 3.05174 13.2624 3 12.9998 3H11.0001" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.99993 0.999512V2.99975M4.99993 2.99975C4.99993 2.46926 5.21067 1.96049 5.58578 1.58537C5.9609 1.21025 6.46967 0.999512 7.00017 0.999512H9.00041C9.53091 0.999512 10.0397 1.21025 10.4148 1.58537C10.7899 1.96049 11.0007 2.46926 11.0007 2.99975C11.0007 3.53025 10.7899 4.03902 10.4148 4.41414C10.0397 4.78925 9.53091 4.99999 9.00041 4.99999H7.00017C6.46967 4.99999 5.9609 4.78925 5.58578 4.41414C5.21067 4.03902 4.99993 3.53025 4.99993 2.99975Z" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.99993 8.99951H8.99985" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M4.99993 12.9995H11.0001" stroke="#001F53" stroke-width="1.12499" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
        : <PatientResultIcon {...props} />,
      onClick: () => history.push("/access/patients"),
    },
    // {
    //   title: "Post-Medical Documentation",
    //   Icon: () => <PostMedicalIcon />,
    // },
  ];

  return (
    <div
      className={`flex flex-col md:flex-row md:justify-between px-3 overflow-auto`}
    >
      {isMobile && (
        <div className="w-full text-[#001F53] py-3">
          <CustomText
            text={"Dashboard"}
            fontWeight="font-bold"
            fontSize="md:text-base text-xs"
          />
        </div>
      )}
      <div
        id="step1"
        className="w-full lg:w-2/6 md:h-4/6 bg-white-300 border border-[#F0F0F0] md:border-none rounded-[4px]"
      >

        {ActionButtons.filter((button: any) => {
          if (
            getActionsForSection(dataConstants.FeatureFlagSectionKeys.DASHBOARD)
              .length === 0
          )
            return false;
          if (isPLUUser)
            return ![
              dataConstants.SectionActionKeys.ATTACH_DOCUMENT,
              dataConstants.SectionActionKeys.NEW_ONLINE_ORDER,
              dataConstants.SectionActionKeys.VIEW_PATIENT_RESULT,
            ].includes(button.key);
          return getActionsForSection(
            dataConstants.FeatureFlagSectionKeys.DASHBOARD
          ).find((action: any) => action.key === button.key)?.isActive;
        }).map((button: any, index: number) => (
          <DashboardActionButton
            key={button.title}
            gradientInfo={button.gradientInfo}
            description={button.description}
            toolTipInfo={
              !!button.gradientInfo
                ? {
                  tooltipText,
                  id: "Online Order button",
                  required: true,
                  showLeft: true,
                }
                : undefined
            }
            title={button.title}
            Icon={button.Icon}
            // disabled={canCreateOrder ? false : true}
            onClick={() => (!isTourActive ? handleButtonClick(button) : "")}
          />
        ))}
      </div>
      {isMobile && (
        <div className="w-full text-[#001F53] py-3">
          <CustomText
            text={"Updates"}
            fontWeight="font-bold"
            fontSize="md:text-base text-xs"
          />
        </div>
      )}
      <div
        className={`md:flex flex-wrap  mb-3 md:justify-center items-center border border-[#F0F0F0] rounded-[4px] md:rounded-none md:border-none md:gap-8 md:max-h-[500px] w-full lg:ml-0 lg:mr-5 md:mt-5 lg:w-4/6 lg:justify-end`}
      >
        {CardsList.filter((card: any, index: number) => {
          let pluCardsOnly = ["Completed Orders", "Submitted"];
          if (isPLUUser) {
            return pluCardsOnly.includes(card.title);
          }
          return card.title !== "Submitted";
        }).map((card: any, index: number) => (
          <OverviewCard key={card.title} hideBorder={(index + 1) === CardsList.length} {...card} loading={isLoading} isMobile={isMobile} />
        ))}
      </div>
    </div>
  );
}

function Dashboard(): JSX.Element {
  const oktaAuth = useOktaAuth();
  const history = useHistory();
  const { isSectionActive, getActionsForSection } = useFeatureFlags();
  const [canCreateOrder, setCanCreateOrder] = useState<boolean>(false);
  const { isLoading, data } = useDashboardStatus();
  const { data: profile, isLoading: fetchingProfile } = useProfile();
  const isMobile = useMobile();

  const {
    state: { selectedPhysician, selectedPhysicianData, selectedOrganization, selectedPathLab },
  } = useContext(ApplicationState);
  const roles = profile?.user?.roles ?? [];

  // Helper function to build approvals URL with current filters
  const buildApprovalsUrl = useCallback(() => {
    const params = new URLSearchParams();

    const statusValue = roles.includes("Collaborative Order Agreement") ||
    roles.includes("Physician") ||
    roles.includes("Office Manager")
    ? 35 : 30;
  params.append("status[]", statusValue.toString());

    // Add current ApplicationState filters
    if (selectedOrganization && selectedOrganization !== 0) {
      params.append("organizationId", selectedOrganization.toString());
    }
    if (selectedPhysician && selectedPhysician !== 0) {
      params.append("physicianId", selectedPhysician.toString());
    }
    if (selectedPathLab && selectedPathLab !== 0) {
      params.append("pathlabId", selectedPathLab.toString());
    }

    return `/access/approvals?${params.toString()}`;
  }, [selectedPhysician, selectedOrganization, selectedPathLab, roles]);

  // 1. Optimize the logic: Only recalculate if 'profile' changes
  const isMoreThan7Days = useMemo(() => {
    const lastLoginStr = profile?.user?.lastLoginAt;

    // Safety check: if profile hasn't loaded, default to false (or true, based on preference)
    if (!lastLoginStr) return false;

    const lastLoginDate = new Date(lastLoginStr);
    const currentDate = new Date();
    const diffInMs = currentDate.getTime() - lastLoginDate.getTime();
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

    return diffInMs > sevenDaysInMs;
  }, [profile]); // Dependency: Only runs when profile updates

  // 2. Optimize the list: Only recreate the array if specific data changes
  const CardsList = useMemo(() => [
    {
      title: "Unsubmitted Orders",
      count: data?.data?.unsubmittedOrders,
      onClick: () => history.push("/access/test-orders?status[]=25"),
      countColor: "text-[#4F6CFE]",
    },
    {
      title: "Pending Approval",
      count: data?.data?.pendingApproval,
      onClick: () => {
        const approvalsUrl = buildApprovalsUrl();
        history.push(approvalsUrl);
      },
      countColor: "text-[#B718B5]",
    },
    {
      title: "Pending Results",
      count: data?.data?.pendingResults,
      onClick: () => history.push("/access/test-orders?status[]=70"),
      countColor: "text-[#AD4FFE]",
    },
    {
      title: "Completed Orders",
      count: data?.data?.completedOrders,
      // Now this text only updates when the calculation above flips
      title2: isMoreThan7Days ? "( in the last 7 days )" : "( in the last 72 hours )",
      onClick: () => history.push("/access/test-orders?status[]=105"),
      countColor: "text-[#1BC472]",
    },
    {
      title: "Submitted",
      count: data?.data?.submitted,
      onClick: () => history.push("/access/test-orders?status[]=40"),
      countColor: "text-[#4F6CFE]",
    },
  ], [data, isMoreThan7Days, history, buildApprovalsUrl]); // Dependencies: Only rebuild if data or the date-logic changes

  const { startTour, isTourActive } = useAdminContext();
  const [showNewReleasePopup, setShowNewReleasePopup] = useState(false);
  const openPopup = () => {
    setShowNewReleasePopup(true);
  };
  const closeReleasePopup = () => {
    setShowNewReleasePopup(false);
  };
  const { visible, setVisible, setPopUp, setPopUpTitle, setOnlineOrder } =
    useContext(PopUpContext);
  let isPLUUser =
    profile &&
    profile?.user?.roles?.includes("PathLab User") &&
    profile?.user?.roles?.length == 1;

  const orderActions = new OnlineOrderActions(
    selectedOrganization,
    selectedPhysician,
    selectedPhysicianData,
    roles,
    profile?.user.id,
    selectedPathLab,
    undefined,
    oktaAuth?.authState?.accessToken?.accessToken
  );
  const tooltipText = orderActions.popUpText();

  useEffect(() => {
    orderActions.allowedToCreateOrder().then((res) => {
      setCanCreateOrder(res);
    });
  }, [selectedOrganization, selectedPathLab, selectedPhysician]);

  const openPaitentFlowPopup = () => {
    setPopUpTitle("Choose or Create a Patient");
    setPopUp(<PatientSelectionPopup oktaAuth={oktaAuth} />);
    setOnlineOrder?.(false);
  };
  const { isPatientPopupEnabled } = useAdminContext();
  const isPLUPhysician =
    roles.includes("Physician") && roles.includes("PathLab User");
  const isPhysicianOnly =
    (roles.length === 1 && roles.includes("Physician")) || isPLUPhysician;
  const [showCustomButton, setShowCustomButton] = useState(true);

  const handleClose = () => setShowCustomButton(false);

  function handleButtonClick(button: any) {
    if (button.gradientInfo) {
      setVisible(!visible);
      setPopUpTitle(
        `Select ${roles?.includes("Collaborative Order Agreement") ? "a PathLab" : ""
        } an organization ${isPhysicianOnly ? "" : "and a clinician"}`
      );
      setPopUp(
        <OrganizationAndClinicianSelection handleNext={openPaitentFlowPopup} />
      );
      // setPopupVisible(true)
    } else button?.onClick?.();
  }

  useEffect(() => {
    if (!isSectionActive("DASHBOARD")) {
      history.push("/unauthorized");
    }
  }, []);

  useEffect(() => {
    if (isPatientPopupEnabled) {
      setVisible(!visible);
      setPopUpTitle("Choose or Create a Patient");
      setPopUp(<CreatePatientPopup oktaAuth={oktaAuth} />);
    } else {
      setVisible(visible);
    }
  }, [isPatientPopupEnabled]);

  return (
    <ErrorBoundary fallbackClassName={"!h-[100vh]"}>
      <AdminContentLayout
        pageTitle="Dashboard | CSTL"
        subHeaderTitle="Dashboard"
        rightSectionClassName="overflow-x-hidden md:overflow-auto"
        subHeaderContainerClassName="md:max-h-[57px] items-center"
        hideTitleAndRedirect={isMobile}
        customButtons={
          showCustomButton
            ? [
              {
                buttonTheme: "primary",
                textInformation: {
                  text: (
                    <NewVersionBanner
                      handleClose={handleClose}
                      openPopup={openPopup}
                    />
                  ),
                  fontWeight: "font-semibold",
                  fontSize: "text-sm",
                },
                onClick: () => openPopup(),
                className:
                  "hidden md:block mr-4 bg-gradient-to-r from-[#31BDBF] to-[#2A6DA9] text-white capitalize rounded-lg",
              },
            ]
            : []
        }
      >
        {showNewReleasePopup && (
          <NewReleasePopup startTour={startTour} onClose={closeReleasePopup} />
        )}
        <ErrorBoundary fallbackClassName="h-[80vh]">
          <RenderLayout
            getActionsForSection={getActionsForSection}
            isMobile={isMobile}
            isPLUUser={isPLUUser}
            CardsList={CardsList}
            isLoading={isLoading || fetchingProfile}
            tooltipText={tooltipText}
            handleButtonClick={handleButtonClick}
            isTourActive={isTourActive}
          />
        </ErrorBoundary>
      </AdminContentLayout>
    </ErrorBoundary>
  );
}

export default Dashboard;
